import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import type {TurnResponse} from '@wandb/weave/lib/WbAgent/WbAgentClient';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {shallowEqual} from 'react-redux';

import {extractProjectsForThread} from '../../../agentChat/buildThread';
import {useWBAgentCrossTabEvents} from '../../../agentChat/crossTabSync';
import {extractErrorMessage} from '../../../agentChat/runtime/errors';
import {mergeThreadSummaries} from '../../../agentChat/store/actions';
import {
  useAgentChatDispatch,
  useStoreSelector,
} from '../../../agentChat/store/context';
import type {
  AgentChatState,
  ThreadSummary,
} from '../../../agentChat/store/thread';
import {parseTurnTitle} from '../threadTitle';

const PAGE_SIZE = 50;

/**
 * useThreadHistory manages paginated fetching of root turns for the chat
 * history sidebar. Provides `loadInitial` (reset + fetch first page),
 * `loadMore` (next page), and `cancelFetch` (abort in-flight requests).
 *
 * Summary thread metadata lands in the agent-chat store via
 * `mergeThreadSummaries`; this hook owns only the ordered list of ids.
 *
 * The returned `ids` is derived: locally-promoted thread ids (from
 * `state.promotionAliases`) are prepended to the fetched page so a
 * brand-new chat appears at the top of the sidebar without a refetch.
 * The derivation reads only the promotion-alias map (NOT every key in
 * `state.threads`), so unrelated store activity like `loadThread` for a
 * historical thread can't displace the sidebar's bucket order.
 */
export function useThreadHistory() {
  const client = useWbAgentClient();
  const dispatch = useAgentChatDispatch();

  const [fetchedIds, setFetchedIds] = useState<string[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const abortRef = useRef<AbortController | null>(null);
  const isFetchingRef = useRef(false);
  const offsetRef = useRef(0);
  const crossTabRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  // `Object.values` order is oldest first, so reverse puts the most recent
  // promotion at the top of the sidebar.
  const promotedThreadIds = useStoreSelector(
    useCallback(
      (s: AgentChatState) => Object.values(s.promotionAliases).reverse(),
      []
    ),
    shallowEqual
  );

  // Ensure promoted ids are always present in the returned list
  const ids = useMemo(() => {
    const fetchedSet = new Set(fetchedIds);
    const promoted = promotedThreadIds.filter(id => !fetchedSet.has(id));
    return [...promoted, ...fetchedIds];
  }, [promotedThreadIds, fetchedIds]);

  // Abort any in-flight request on unmount (e.g. navigation away) to avoid
  // firing state updates into a dead component tree.
  useEffect(
    () => () => {
      abortRef.current?.abort();
      if (crossTabRefreshTimerRef.current !== null) {
        clearTimeout(crossTabRefreshTimerRef.current);
      }
    },
    []
  );

  const fetchPage = useCallback(
    async (offset: number) => {
      if (!client) {
        return;
      }

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      isFetchingRef.current = true;

      setStatus('loading');
      setError(null);

      try {
        // TODO: Offset based pagination may result in duplicates or missing
        // results. Add backend support for cursor-based pagination or filtering
        // by a timestamp to resolve.
        const turns = await client.get(
          '/turns',
          {
            // Fetch across all projects the user can read; history is a
            // cross-project view regardless of the current route.
            entity: undefined,
            project: undefined,
            is_root: true,
            sort_by: 'thread_last_turn_created_at',
            sort_desc: true,
            limit: PAGE_SIZE + 1,
            offset,
          },
          controller.signal
        );

        if (controller.signal.aborted) {
          return;
        }

        const hasMoreResults = turns.length > PAGE_SIZE;
        setHasMore(hasMoreResults);

        const pageTurns = turns.slice(0, PAGE_SIZE);
        const summaries = pageTurns.map(turnToSummary);
        const newIds = summaries.map(s => s.id);

        setFetchedIds(prev => {
          const merged = offset === 0 ? newIds : [...prev, ...newIds];
          // Concurrent inserts between page fetches can return the same
          // row in two pages, so duplicates are removed here.
          return Array.from(new Set(merged));
        });

        dispatch(mergeThreadSummaries(summaries));

        offsetRef.current = offset + newIds.length;
        setStatus('idle');
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          return;
        }

        setError(extractErrorMessage(err));
        setStatus('error');
      } finally {
        // Don't reset if this request was aborted — a newer fetchPage call
        // may have already set isFetchingRef and this must not overwrite it.
        if (!controller.signal.aborted) {
          isFetchingRef.current = false;
        }
      }
    },
    [client, dispatch]
  );

  const cancelFetch = useCallback(() => abortRef.current?.abort(), []);

  const loadInitial = useCallback(() => {
    offsetRef.current = 0;
    setFetchedIds([]);
    setHasMore(true);
    fetchPage(0);
  }, [fetchPage]);

  const scheduleCrossTabRefresh = useCallback(() => {
    if (crossTabRefreshTimerRef.current !== null) {
      clearTimeout(crossTabRefreshTimerRef.current);
    }
    crossTabRefreshTimerRef.current = setTimeout(() => {
      crossTabRefreshTimerRef.current = null;
      loadInitial();
    }, 0);
  }, [loadInitial]);

  const loadMore = useCallback(() => {
    if (isFetchingRef.current || !hasMore) {
      return;
    }

    fetchPage(offsetRef.current);
  }, [fetchPage, hasMore]);

  useWBAgentCrossTabEvents(
    useCallback(
      event => {
        if (event.type === 'turn-aborted' || event.type === 'turn-updated') {
          return;
        }
        scheduleCrossTabRefresh();
      },
      [scheduleCrossTabRefresh]
    )
  );

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        loadInitial();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadInitial]);

  return {
    ids,
    status,
    error,
    hasMore,
    loadInitial,
    loadMore,
    cancelFetch,
  };
}

function turnToSummary(turn: TurnResponse): ThreadSummary {
  const {title, promptSnippet} = parseTurnTitle(turn);
  return {
    id: turn.id,
    rootTurnId: turn.id,
    title,
    promptSnippet,
    updatedAt: Date.parse(turn.updated_at),
    lastActivityAt: Date.parse(
      turn.thread_last_turn_created_at ?? turn.created_at
    ),
    createdAt: Date.parse(turn.created_at),
    projects: extractProjectsForThread(turn),
  };
}
